import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { useDispatch, useStore } from "react-redux";
import { updateDeviceMapAction } from "../actions/device.action";
import * as dev_constants from '../constants/dev_constants';
import {
    clearCounts, getErrorGrpId, isErrorGrpAlreadyExists, showDeviceAsOff, arraySymmetricDiff, isInsideSchedule
} from "../utilities/dev_utils";
import { db, getCheckSession, getIsAdmin } from "./auth";
import { getSchedule } from "../query/dim-schedule.query";
import { deviceErrorEventCode, deviceErrorEventCodeOrder, typeFilter } from "../constants/constants";
import DashboardDataService from "./dashboard-data";
import { NotificationManager } from "react-notifications";

let deviceDataInstance;
let devicesMap = new Map();
let devIdCache = new Map();
let devicesMapNonSlc = new Map();
let unsub = undefined;
let completionHandler = null;
let updateHandler = null;
let currentPageIndex = 1;
let currentPageSize = 10;
let deviceData = [];
let allDeviceData = [];
let schedule = null;
export let isInitialLoadCompleted = false;
let hasReceivedServerSnapshot = false;

let filters = {
    zone: 0,
    ward: 0,
    ccms: 0,
    slc_status: typeFilter[0],
    status: 0,
}

let pageFilters = {
    zone: 0,
    ward: 0,
    ccms: 0,
    status: 0,
}

let countsObject = {
    slcCnt: 0,
    slcOnCnt: 0,
    slcOffCnt: 0,
    slcErrCnt: 0,
    lmpCnt: 0,
    lmpOnCnt: 0,
    lmpOffCnt: 0,
    modes: {
        autoCnt: 0,
        manCnt: 0,
        bypCnt: 0
    },
    inpFaults: {
        lvCnt: 0,
        hvCnt: 0,
        pfCnt: 0,
        isofCnt: 0
    },
    outFaults: {
        tripCnt: 0,
        hcCnt: 0,
        cfCnt: 0
    },
    othFaults: {
        emfCnt: 0,
        doCnt: 0,
        lbCnt: 0,
        inaCnt: 0
    },
    errGrpCounts: Array(dev_constants.EventGrpMax).fill(0),
};

const emptyDevice = {
    uid: null,
    dev_id: null,
    dev_st: dev_constants.DeviceStatusNone,
    dev_md: dev_constants.DeviceModeNone,
    lmp_cnt: 0,
    lmp_off_cnt: 0,
    err_cnt: 0,
    errors: [],
}

class DeviceData {
    constructor() {
        if (deviceDataInstance) {
            throw new Error("Instance already created");
        }
        deviceDataInstance = this;
    }

    getInstance() {
        return deviceDataInstance;
    }

    initService(completionHandlerArg, updateHandlerArg) {
        return new Promise((resolve) => {
            this.reset();
            completionHandler = () => {
                if (completionHandlerArg) completionHandlerArg();
                resolve();
            };
            updateHandler = updateHandlerArg;
            if (devicesMap.size > 0) {
                if (completionHandler) completionHandler();
            } else {
                this.fetchAllDevices();
            }
        });
    }

    getScheduleData() {
        return getSchedule().then((data) => {
            schedule = data;
            console.log("device-data: Schedule loaded, recounting all devices...");
            this.recountAll();
        }).catch(err => console.log("device-data: getSchedule error", err));
    }

    deinit() {
        if (unsub) {
            unsub();
            unsub = undefined;
        }
        devicesMap = new Map();
        devIdCache = new Map();
        devicesMapNonSlc = new Map();
        deviceData = [];
        allDeviceData = [];
        schedule = null;
        completionHandler = null;
        updateHandler = null;
        this.reset();
    }

    reset() {
        currentPageIndex = 1;
        currentPageSize = 10;
        isInitialLoadCompleted = false;
        hasReceivedServerSnapshot = false;
        filters = {
            zone: 0,
            ward: 0,
            ccms: 0,
        }
        pageFilters = {
            zone: 0,
            ward: 0,
            ccms: 0,
        }
    }

    setPageIndex(index) {
        currentPageIndex = index;
    }

    getWithSLCCount() {
        var data = [...devicesMap.values()]
        return data.length;
    }

    getPageIndex() {
        return currentPageIndex;
    }

    setPageSize(index) {
        currentPageSize = index;
    }

    getPageSize() {
        return currentPageSize;
    }

    getCounts() {
        var mockData = DashboardDataService.getInstance().getUpdatedDashboardData(countsObject, filters.zone, filters.ward);
        return mockData;
    }

    iterateDevices(devHandler) {
        for (const devData of devicesMap.values()) {
            devHandler(devData);
        }
    }

    getDevices() {
        return devicesMap;
    }

    getDevice(devId) {
        if (devId === undefined | null | "" | 0) {
            return null;
        }
        return devicesMap.get(devId);
    }

    getDeviceByDevId(devId) {
        if (devId === undefined | null | "" | 0) {
            return null;
        }
        for (const devData of devicesMap.values()) {
            if (devData.dev_id === devId) {
                return devData;
            }
        }
        return null;
    }

    getSlcDevice(devId) {
        if (!devId) {
            return null;
        }
        
        // Fast path O(1) lookup
        if (devicesMap.has(devId)) {
            return devicesMap.get(devId);
        }

        if (devIdCache.has(devId)) {
            const cachedUid = devIdCache.get(devId);
            if (cachedUid === null) {
                return null;
            }
            if (devicesMap.has(cachedUid)) {
                return devicesMap.get(cachedUid);
            }
        }
        
        for (const devData of devicesMap.values()) {
            if (devData.uid === devId) {
                return devData;
            }
        }
        // Fallback: Check dev_id, name, and mac_id
        for (const devData of devicesMap.values()) {
            if (devData.dev_id === devId || devData.name === devId || devData.mac_id === devId || devData.ccms_id === devId) {
                devIdCache.set(devId, devData.uid);
                return devData;
            }
        }
        // Fallback 2: Dash-insensitive match
        const normalizedDevId = devId.replace(/-/g, '').toLowerCase();
        for (const devData of devicesMap.values()) {
            const normalizedUid = devData.uid ? devData.uid.replace(/-/g, '').toLowerCase() : "";
            const normalizedDev = devData.dev_id ? devData.dev_id.replace(/-/g, '').toLowerCase() : "";
            if (normalizedUid === normalizedDevId || normalizedDev === normalizedDevId) {
                devIdCache.set(devId, devData.uid);
                return devData;
            }
        }
        
        // Negative cache to prevent repeated slow lookups for non-existent devices
        devIdCache.set(devId, null);
        return null;
    }

    getWorkingCCMS() {
        let countsObject = {
            slcCnt1: 0,
            slcOnCnt: 0,
            slcOffCnt: 0,
        }
        for (const devData of devicesMap.values()) {
            if (!this.shouldCount(devData, filters.zone, filters.ward)) continue;
            countsObject.slcCnt1++;
            const effectiveStatus = this.getSlcCountStatus(devData);
            if (effectiveStatus === dev_constants.DeviceStatusOn) {
                countsObject.slcOnCnt++;
            } else if (effectiveStatus === dev_constants.DeviceStatusOff) {
                countsObject.slcOffCnt++;
            }
        }
        return countsObject;
    }

    getDeviceByPID(pid) {
        if (pid === undefined | null | "" | 0) {
            return null;
        }
        for (const devData of devicesMap.values()) {
            if (devData.uid === pid) {
                return devData;
            }
        }
        return null;
    }

    isDeviceIdOn(devId) {
        let deviceData = devicesMap.get(devId);
        return this.isDeviceOn(deviceData);
    };

    isDeviceOn(deviceData) {
        let devOn = false;
        if (deviceData !== undefined) {
            if (deviceData.dev_st === dev_constants.DeviceStatusOn) {
                devOn = true;
            }
        }
        return devOn;
    };

    getErrorGroupCount(errGrpCode) {
        if (errGrpCode >= 0 && errGrpCode < dev_constants.EventGrpMax) {
            let count = countsObject.errGrpCounts[errGrpCode];
            if (count !== undefined) {
                return count;
            }
        }
        return 0;
    }

    async setDeviceFilter(zoneId, wardId, ccmsId = 0, statusId = 0, filter = typeFilter[0]) {
        let filterChanged = false;

        if (filters.zone != zoneId) {
            filters.zone = zoneId;
            filterChanged = true;
        }
        if (filters.ward != wardId) {
            filters.ward = wardId;
            filterChanged = true;
        }
        if (filters.slc_status != filter) {
            filters.slc_status = filter;
            filterChanged = true;
        }
        if (filters.ccms != ccmsId) {
            filters.ccms = ccmsId;
            filterChanged = true;
        }
        if (filters.status != statusId) {
            filters.status = statusId;
            filterChanged = true;
        }

        if (filterChanged) {
            await this.recountAll();
        }
    }

    getDeviceFilter() {
        return filters;
    }

    async setPageFilter(zoneId, wardId, ccmsId = 0, statusId = 0) {
        if (pageFilters.zone != zoneId) {
            pageFilters.zone = zoneId;
        }
        if (pageFilters.ward != wardId) {
            pageFilters.ward = wardId;
        }
        if (pageFilters.ccms != ccmsId) {
            pageFilters.ccms = ccmsId;
        }
        if (pageFilters.status != statusId) {
            pageFilters.status = statusId;
        }
    }

    getPageFilter() {
        return pageFilters;
    }

    // ─── FIX 2: New method — query devices by explicit zone/ward/ccms args
    // without touching the shared singleton filter state.
    // Use this from LiveMeterFeed instead of setDeviceFilter + getPagedDevicesByFilter.
    async getPagedDevicesByExplicitFilter(zoneId, wardId, ccms = 0, status = 0, page = 1, rowCount = 10) {
        let result = {
            deviceListIds: [{ label: "All", value: 0 }],
            deviceList: [],
            allDeviceList: [],
            errorDeviceListIds: [{ label: "All", value: 0 }],
            errorDeviceList: [],
            pageNum: page,
            totalRecords: 0,
            totalPages: 0
        };

        for (const devData of devicesMap.values()) {
            if (this.shouldCount(devData, zoneId, wardId)) {
                result.deviceListIds.push({ label: devData.dev_id, value: devData.uid });

                if ((ccms === 0 || devData.uid === ccms) && (status === 0 || devData.dev_st === status)) {
                    result.deviceList.push(devData);

                    if (devData.err_cnt > 0) {
                        result.errorDeviceListIds.push({ label: devData.dev_id, value: devData.uid });
                        result.errorDeviceList.push(devData);
                    }
                }
            }
        }

        result.totalRecords = result.deviceList.length;

        if (result.totalRecords > 0) {
            result.totalPages = Math.ceil(result.totalRecords / rowCount);

            let startIndex = (page - 1) * rowCount;
            if (startIndex >= result.totalRecords) {
                startIndex = 0;
                result.pageNum = 1;
            }

            let endIndex = startIndex + rowCount;
            if (endIndex > result.totalRecords) {
                endIndex = result.totalRecords;
            }
            result.allDeviceList = result.deviceList;
            result.deviceList = result.deviceList.slice(startIndex, endIndex);
        }

        return result;
    }

    // ─── FIX 2: New method — get device uid list by explicit zone/ward args
    // without reading from or writing to the shared singleton filter state.
    async getDeviceUidListByZoneWard(zoneId, wardId) {
        let deviceListIds = [{ label: "All", value: 0 }];
        for (const devData of devicesMap.values()) {
            if (this.shouldCount(devData, zoneId, wardId)) {
                deviceListIds.push({ label: devData.dev_id, value: devData.uid });
            }
        }
        return deviceListIds;
    }

    async getDevicesByFilter(zoneId, wardId, ccms = 0) {
        let result = {
            deviceListIds: [{ label: "All", value: 0 }],
            deviceList: [],
            errorDeviceListIds: [{ label: "All", value: 0 }],
            errorDeviceList: []
        };
        let offCount = 0;
        let slcOffCount = DashboardDataService.getInstance().getDashboardDataByKey(zoneId, wardId, "slcOffCnt");
        let slcOnCount = DashboardDataService.getInstance().getDashboardDataByKey(zoneId, wardId, "slcOnCnt");

        for (const devData of devicesMap.values()) {
            if (this.shouldCount(devData, zoneId, wardId)) {
                result.deviceListIds.push({ label: devData.dev_id, value: devData.dev_id });
                if (ccms === 0 || devData.uid === ccms) {
                    result.deviceList.push(devData)
                    if (devData.err_cnt > 0) {
                        result.errorDeviceListIds.push({ label: devData.dev_id, value: devData.dev_id });
                        result.errorDeviceList.push(devData);
                    }
                }
            }
        }
        return (result);
    }

    async getNonSlcAndSlc(zoneId, wardId, slc_status = typeFilter[0]) {
        let result = {
            deviceList: [],
            errorDeviceList: []
        };

        let offCount = 0;
        let slcOffCount = DashboardDataService.getInstance().getDashboardDataByKey(zoneId, wardId, "slcOffCnt");
        let slcOnCount = DashboardDataService.getInstance().getDashboardDataByKey(zoneId, wardId, "slcOnCnt");

        for (const devData of devicesMap.values()) {
            if (this.shouldCount(devData, zoneId, wardId)) {
                if (slcOffCount == 0) {
                    devData.dev_st = dev_constants.DeviceStatusOn;
                } else if (slcOnCount == 0) {
                    devData.dev_st = dev_constants.DeviceStatusOff;
                } else if (slcOffCount > 0) {
                    if (offCount >= slcOffCount) {
                        devData.dev_st = dev_constants.DeviceStatusOn;
                    } else {
                        devData.dev_st = dev_constants.DeviceStatusOff;
                        offCount++;
                    }
                }
                result.deviceList.push(devData);

                if (devData.err_cnt > 0) {
                    result.errorDeviceList.push(devData);
                }
            }
        }

        return result;
    }

    async getDevicesByUidFilter(zoneId, wardId, ccms = 0) {
        let result = {
            deviceListIds: [{ label: "All", value: 0 }],
            deviceList: [],
            errorDeviceListIds: [{ label: "All", value: 0 }],
            errorDeviceList: []
        };
        let offCount = 0;
        let slcOffCount = DashboardDataService.getInstance().getDashboardDataByKey(zoneId, wardId, "slcOffCnt");
        let slcOnCount = DashboardDataService.getInstance().getDashboardDataByKey(zoneId, wardId, "slcOnCnt");

        for (const devData of devicesMap.values()) {
            if (this.shouldCount(devData, zoneId, wardId)) {
                result.deviceListIds.push({ label: devData.dev_id, value: devData.uid });
                if (ccms === 0 || devData.uid === ccms) {
                    if (slcOffCount == 0) {
                        devData.dev_st = dev_constants.DeviceStatusOn
                    } else if (slcOnCount == 0) {
                        devData.dev_st = dev_constants.DeviceStatusOff
                    } else if (slcOffCount > 0) {
                        if (offCount >= slcOffCount) {
                            devData.dev_st = dev_constants.DeviceStatusOn
                        } else {
                            devData.dev_st = dev_constants.DeviceStatusOff
                            offCount++;
                        }
                    }
                    result.deviceList.push(devData)
                    if (devData.err_cnt > 0) {
                        result.errorDeviceListIds.push({ label: devData.dev_id, value: devData.uid });
                        result.errorDeviceList.push(devData);
                    }
                }
            }
        }
        return (result);
    }

    async getPagedDevicesByFilter(zoneId, wardId, ccms = 0, status, page = 1, rowCount = 10) {
        deviceData = []
        allDeviceData = []
        let result = {
            deviceListIds: [{ label: "All", value: 0 }],
            deviceList: [],
            allDeviceList: [],
            errorDeviceListIds: [{ label: "All", value: 0 }],
            errorDeviceList: [],
            pageNum: page,
            totalRecords: 0,
            totalPages: 0
        };

        for (const devData of devicesMap.values()) {
            if (this.shouldCount(devData, zoneId, wardId)) {
                result.deviceListIds.push({ label: devData.dev_id, value: devData.uid });

                if ((ccms === 0 || devData.uid === ccms) && (status === 0 || devData.dev_st === status)) {
                    result.deviceList.push(devData);

                    if (devData.err_cnt > 0) {
                        result.errorDeviceListIds.push({ label: devData.dev_id, value: devData.uid });
                        result.errorDeviceList.push(devData);
                    }
                }
            }
        }

        result.totalRecords = result.deviceList.length;

        if (result.totalRecords > 0) {
            result.totalPages = Math.ceil(result.totalRecords / rowCount);

            let startIndex = (page - 1) * rowCount;
            if (startIndex >= result.totalRecords) {
                startIndex = 0;
                result.pageNum = 1;
            }

            let endIndex = startIndex + rowCount;
            if (endIndex > result.totalRecords) {
                endIndex = result.totalRecords;
            }
            result.allDeviceList = result.deviceList;
            allDeviceData = result.deviceList;
            deviceData = result.deviceList.slice(startIndex, endIndex);
            result.deviceList = result.deviceList.slice(startIndex, endIndex);
        }

        return result;
    }

    async fetchDashboardData() {
        try {
            const docRef = doc(db(), "settings", "dashboard_data");
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                console.log("Document data:", docSnap.data());
            } else {
                console.log("No such document!");
            }
        } catch (err) {
            NotificationManager.error("Unable to load device data, please try after some time.", "", 3000);
        }
    }

    async fetchAllDevices() {
        if (!db()) {
            console.log("DeviceDataService: db is null, aborting fetchAllDevices");
            return;
        }
        clearCounts(countsObject, devicesMap);

        try {
            let newRequest = true;
            let lastCallbackTime = new Date();
            let avgTimeMilliSecs = 0;

            const q = await query(collection(db(), "devices"),
                where("dev_ty", "==", dev_constants.DeviceTypeSLC),
            );

            const deviceStartTime = Date.now();
            unsub = onSnapshot(q, (snapshot) => {
                let addedCount = 0;
                let modifiedCount = 0;
                let removedCount = 0;

                snapshot.docChanges().forEach((change) => {
                    if (change.type === "added") addedCount++;
                    if (change.type === "modified") modifiedCount++;
                    if (change.type === "removed") removedCount++;

                    let docData = change.doc.data();
                    docData.docId = change.doc.id;
                    
                    if (newRequest) {
                        let currTime = new Date();
                        let milliSecsSinceLastResp = currTime.getTime() - lastCallbackTime.getTime();
                        if (devicesMap.size === 0) {
                            avgTimeMilliSecs = milliSecsSinceLastResp;
                        } else {
                            avgTimeMilliSecs = (avgTimeMilliSecs + milliSecsSinceLastResp) / 2;
                        }
                        lastCallbackTime = new Date();
                    }

                    if (docData["dev_id"] === undefined || docData["dev_id"] === "") {
                        this.addToNonSLCDevicesMap(docData, change.type);
                    } else if (docData["ins_st"] === dev_constants.InstallStatusEnabled) {
                        if (this.shouldCount(docData, filters.zone, filters.ward)) {
                            if (change.type === "added") {
                                this.addCountsForNewDevice(docData);
                            } else if (change.type === "modified") {
                                this.modifyCountsForExistingDevice(docData);
                            } else if (change.type === "removed") {
                                this.deleteCountsForExistingDevice(docData);
                            }
                        } else {
                            this.addToDevicesMap(docData, change.type);
                        }

                        if (!newRequest && updateHandler != null) {
                            updateHandler(docData, change.type, deviceData, allDeviceData);
                        }
                    }
                });


                if (newRequest) {
                    const timeTaken = Date.now() - deviceStartTime;
                    console.log(`[PERF] Initial Devices Snapshot Fetch: ${timeTaken}ms (From Cache: ${snapshot.metadata.fromCache}). Total SLCs fetched: ${snapshot.size}`);
                    newRequest = false;
                    isInitialLoadCompleted = true;
                    if (!snapshot.metadata.fromCache) {
                        hasReceivedServerSnapshot = true;
                    }
                    this.recountAll();
                    if (completionHandler) {
                        completionHandler();
                    }
                } else if (!hasReceivedServerSnapshot && !snapshot.metadata.fromCache) {
                    hasReceivedServerSnapshot = true;
                    console.log(`[PERF] First Devices Server Snapshot Received. Triggering UI refresh.`);
                    this.recountAll();
                    if (completionHandler) {
                        completionHandler();
                    }
                }

            }, (error) => {
                setTimeout(() => this.reconnectDeviceSnapshot(), 30000);
            });

            const checkLastCallbackTimerId = setInterval(() => checkLastCallback(), 1000);

            const checkLastCallback = () => {
                let waitMilliSecs = 0;
                let currTime = new Date();
                let milliSecsSinceLastResp = currTime.getTime() - lastCallbackTime.getTime();

                if (devicesMap.size === 0) {
                    waitMilliSecs = 30000;
                } else {
                    waitMilliSecs = avgTimeMilliSecs * 2;
                    if (waitMilliSecs < 1000) {
                        waitMilliSecs = 1000;
                    }
                }

                if (milliSecsSinceLastResp > waitMilliSecs) {
                    newRequest = false;
                    isInitialLoadCompleted = true;
                    this.recountAll();
                    clearInterval(checkLastCallbackTimerId);
                    if (devicesMap.size > 0 && updateHandler != null) {
                        updateHandler(undefined, "initial");
                    }
                }
            }

            setTimeout(() => DeviceDataService.getInstance().checkData(), 60000);
        } catch (err) {
            console.log("device-data.js", err)
            NotificationManager.error("Unable to load device data, please try after some time.", "", 3000);
        }
    }

    /** Internal Functions */

    refreshSlcCounts() {
        this.recountAll();
        if (updateHandler) {
            updateHandler();
        }
    }

    async recountAll() {
        clearCounts(countsObject, undefined);
        let recount = 0;
        if (filters.slc_status === typeFilter[0]) {
            for (const devData of [...devicesMap.values(), ...devicesMapNonSlc.values()]) {
                if (this.shouldCount(devData, filters.zone, filters.ward)) {
                    this.refreshCountsForDevice(devData);
                    recount++;
                }
            };
        } else if (filters.slc_status === typeFilter[1]) {
            for (const devData of devicesMap.values()) {
                if (this.shouldCount(devData, filters.zone, filters.ward)) {
                    this.refreshCountsForDevice(devData);
                    recount++;
                }
            };
        } else if (filters.slc_status === typeFilter[2]) {
            for (const devData of devicesMapNonSlc.values()) {
                if (this.shouldCount(devData, filters.zone, filters.ward)) {
                    this.refreshCountsForDevice(devData);
                    recount++;
                }
            };
        }
    }

    refreshCountsForDevice(deviceData) {
        this.updateCountsForDevice(emptyDevice, deviceData);
    }

    shouldCount(deviceData, zoneId = 0, wardId = 0) {
        let result = false;
        if (zoneId === 0) {
            result = true;
        } else {
            if (wardId === 0) {
                if (zoneId === deviceData.zn_id || zoneId === deviceData.zn) {
                    result = true;
                }
            } else {
                if ((zoneId === deviceData.zn_id || zoneId === deviceData.zn) && (wardId === deviceData.wd_id || wardId === deviceData.wd)) {
                    result = true;
                }
            }
        }
        return result;
    }

    checkData() {
        if (devicesMap.size === 0) {
            if (unsub !== undefined) {
                unsub();
            }
            DeviceDataService.getInstance().fetchAllDevices();
        }
    }

    reconnectDeviceSnapshot() {
        if (unsub !== undefined) {
            unsub();
        }
        clearCounts(countsObject, devicesMap);
        DeviceDataService.getInstance().fetchAllDevices();
        // setTimeout(() => this.printCounts(), 5000); // printCounts is not defined
    }

    addToDevicesMap(newDeviceData, changeType) {
        let oldDeviceData = devicesMap.get(newDeviceData.uid);
        let isDevAlrdyCached = (oldDeviceData !== undefined);

        if (changeType === "added") {
            if (isDevAlrdyCached) {
                devicesMap.delete(oldDeviceData.uid)
            }
            devicesMap.set(newDeviceData.uid, newDeviceData)
        } else if (changeType === "modified") {
            if (isDevAlrdyCached) {
                devicesMap.set(newDeviceData.uid, newDeviceData)
            }
        } else if (changeType === "removed") {
            if (isDevAlrdyCached) {
                devicesMap.delete(oldDeviceData.uid)
            }
        }
    }

    addToNonSLCDevicesMap(newDeviceData, changeType) {
        let oldDeviceData = devicesMapNonSlc.get(newDeviceData.uid);
        let isDevAlrdyCached = (oldDeviceData !== undefined);

        if (changeType === "added") {
            if (isDevAlrdyCached) {
                devicesMapNonSlc.delete(oldDeviceData.uid)
            }
            devicesMapNonSlc.set(newDeviceData.uid, newDeviceData)
        } else if (changeType === "modified") {
            if (isDevAlrdyCached) {
                devicesMapNonSlc.set(newDeviceData.uid, newDeviceData)
            }
        } else if (changeType === "removed") {
            if (isDevAlrdyCached) {
                devicesMapNonSlc.delete(oldDeviceData.uid)
            }
        }
    }

    addCountsForNewDevice(newDeviceData) {
        let oldDeviceData = devicesMap.get(newDeviceData.uid);
        let isDevAlrdyCached = (oldDeviceData !== undefined);

        if (isDevAlrdyCached) {
            // should not come here
        } else {
            this.updateCountsForDevice(emptyDevice, newDeviceData);
            devicesMap.set(newDeviceData.uid, newDeviceData)
        }
    }

    modifyCountsForExistingDevice(newDeviceData) {
        let oldDeviceData = devicesMap.get(newDeviceData.uid);
        let isDevAlrdyCached = (oldDeviceData !== undefined);

        if (isDevAlrdyCached) {
            this.updateCountsForDevice(oldDeviceData, newDeviceData);
            devicesMap.set(newDeviceData.uid, newDeviceData)
        }
    }

    updateCountsForDevice(oldDeviceData, newDeviceData) {
        this.updateSlcCounts(oldDeviceData, newDeviceData);
        this.updateLampCounts(oldDeviceData, newDeviceData);
        this.updateModeCounts(oldDeviceData, newDeviceData);
        this.updateErrorCounts(oldDeviceData, newDeviceData);
        this.updateErrorGroupCounts(oldDeviceData, newDeviceData);
    }

    getSlcCountStatus(slcData) {
        if (!slcData || !slcData.uid) return dev_constants.DeviceStatusNone;
        
        if (getIsAdmin()) {
            return slcData.dev_st;
        }

        const isInside = DashboardDataService.getInstance().isGlobalScheduleActive();
        const currentTime = new Date();
        const slcLastSeen = slcData.ls ? new Date(slcData.ls.seconds * 1000) : new Date(0);
        const diffMinutes = (currentTime - slcLastSeen) / 1000 / 60;

        if (isInside) {
            if (slcData.dev_st === dev_constants.DeviceStatusOn) {
                return dev_constants.DeviceStatusOn;
            } else {
                if (diffMinutes < 35) {
                    return slcData.dev_st;
                } else {
                    return dev_constants.DeviceStatusOn; // Active logic defaults to ON if inactive for >35m in schedule
                }
            }
        } else {
            return dev_constants.DeviceStatusOff;
        }
    }

    updateSlcCounts(oldDeviceData, newDeviceData) {
        if (oldDeviceData.uid !== newDeviceData.uid) {
            countsObject.slcCnt++;
        }

        const oldStatus = this.getSlcCountStatus(oldDeviceData);
        const newStatus = this.getSlcCountStatus(newDeviceData);

        if (newStatus === dev_constants.DeviceStatusOn) {
            if (oldStatus === dev_constants.DeviceStatusOff || oldStatus === dev_constants.DeviceStatusNone) {
                countsObject.slcOnCnt++;
                if (oldStatus === dev_constants.DeviceStatusOff && countsObject.slcOffCnt > 0) {
                    countsObject.slcOffCnt--;
                }
            }
        } else if (newStatus === dev_constants.DeviceStatusOff) {
            if (oldStatus === dev_constants.DeviceStatusOn || oldStatus === dev_constants.DeviceStatusNone) {
                countsObject.slcOffCnt++;
                if (oldStatus === dev_constants.DeviceStatusOn && countsObject.slcOnCnt > 0) {
                    countsObject.slcOnCnt--;
                }
            }
        }
    }

    updateLampCounts(oldDeviceData, newDeviceData) {
        if (newDeviceData.lmp_cnt === undefined) newDeviceData.lmp_cnt = 0;

        let modifiedLmpCnt = newDeviceData.lmp_cnt - oldDeviceData.lmp_cnt;
        countsObject.lmpCnt += modifiedLmpCnt;

        if (newDeviceData.lmp_off_cnt === undefined) newDeviceData.lmp_off_cnt = 0;

        let modifiedLmpOffCnt = newDeviceData.lmp_off_cnt - oldDeviceData.lmp_off_cnt;
        countsObject.lmpOffCnt += modifiedLmpOffCnt;

        let modifiedLmpOnCnt = (newDeviceData.lmp_cnt - newDeviceData.lmp_off_cnt) - (oldDeviceData.lmp_cnt - oldDeviceData.lmp_off_cnt);
        countsObject.lmpOnCnt += modifiedLmpOnCnt;
    }

    updateModeCounts(oldDeviceData, newDeviceData) {
        switch (newDeviceData.dev_md) {
            case dev_constants.DeviceModeAuto:
                if (oldDeviceData.dev_md !== dev_constants.DeviceModeAuto) {
                    countsObject.modes.autoCnt++;
                    if (oldDeviceData.dev_md === dev_constants.DeviceModeManual) {
                        if (countsObject.modes.manCnt > 0) countsObject.modes.manCnt--;
                    } else if (oldDeviceData.dev_md === dev_constants.DeviceModeBypass) {
                        if (countsObject.modes.bypCnt > 0) countsObject.modes.bypCnt--;
                    }
                }
                break;
            case dev_constants.DeviceModeManual:
                if (oldDeviceData.dev_md !== dev_constants.DeviceModeManual) {
                    countsObject.modes.manCnt++;
                    if (oldDeviceData.dev_md === dev_constants.DeviceModeAuto) {
                        if (countsObject.modes.autoCnt > 0) countsObject.modes.autoCnt--;
                    } else if (oldDeviceData.dev_md === dev_constants.DeviceModeBypass) {
                        if (countsObject.modes.bypCnt > 0) countsObject.modes.bypCnt--;
                    }
                }
                break;
            case dev_constants.DeviceModeBypass:
                if (oldDeviceData.dev_md !== dev_constants.DeviceModeBypass) {
                    countsObject.modes.bypCnt++;
                    if (oldDeviceData.dev_md === dev_constants.DeviceModeAuto) {
                        if (countsObject.modes.autoCnt > 0) countsObject.modes.autoCnt--;
                    } else if (oldDeviceData.dev_md === dev_constants.DeviceModeManual) {
                        if (countsObject.modes.manCnt > 0) countsObject.modes.manCnt--;
                    }
                }
                break;
            default:
                break;
        }
    }

    updateErrorCounts(oldDeviceData, newDeviceData) {
        if (newDeviceData.err_cnt > 0) {
            if ((oldDeviceData.err_cnt === 0) || (oldDeviceData.err_cnt === undefined)) {
                countsObject.slcErrCnt++;
            }
        } else {
            if (oldDeviceData.err_cnt > 0) {
                if (countsObject.slcErrCnt > 0) countsObject.slcErrCnt--;
            }
        }
    }

    updateErrorGroupCounts(oldDeviceData, newDeviceData) {
        let errDiff = arraySymmetricDiff(oldDeviceData.errors, newDeviceData.errors);

        const grpIdSet = new Set();

        errDiff.added.forEach(errCode => {
            this.updateErrorCountForCode(oldDeviceData, newDeviceData, errCode, true, grpIdSet);
        });

        errDiff.removed.forEach(errCode => {
            this.updateErrorCountForCode(oldDeviceData, newDeviceData, errCode, false, grpIdSet);
        });
    }

    updateErrorCountForCode(oldDeviceData, newDeviceData, errorCode, isAdded, grpIdSet) {
        let grpId = getErrorGrpId(errorCode);

        if ((grpId === -1) || (grpId >= dev_constants.EventGrpMax)) {
            // These are expected for state events like 1001 (PowerOff) or 1004 (ManualMode)
            // which are passed in the errors array but don't map to a specific error group.
            return;
        }

        let grpInfo = dev_constants.errGrpInfoArray[grpId];
        if (grpInfo === undefined) {
            console.log("invalid grp id", grpId);
            return;
        }

        countsObject.errGrpCounts[grpId] += this.updateErrorCountForGroup(oldDeviceData, newDeviceData, errorCode,
            grpInfo, countsObject.errGrpCounts[grpId], isAdded, grpIdSet);

        this.updateErrorGroupsStruct(grpId, countsObject.errGrpCounts[grpId]);
    }

    updateErrorCountForGroup(oldDeviceData, newDeviceData, errorCode, grpInfo, currVal, isAdded, grpIdSet) {
        let changeVal = 0;

        if (isAdded) {
            if (!grpIdSet.has(grpInfo.grpId) && !isErrorGrpAlreadyExists(oldDeviceData, grpInfo.errCodes)) {
                grpIdSet.add(grpInfo.grpId);
                changeVal = 1;
            }
        } else {
            if (!grpIdSet.has(grpInfo.grpId) && !isErrorGrpAlreadyExists(newDeviceData, grpInfo.errCodes)) {
                if (currVal > 0) changeVal = -1;
                grpIdSet.add(grpInfo.grpId);
            }
        }

        return changeVal;
    }

    updateErrorGroupsStruct(errGroup, count) {
        switch (errGroup) {
            case dev_constants.EventGrpLowVoltage:
                countsObject.inpFaults.lvCnt = count;
                break;
            case dev_constants.EventGrpHighVoltage:
                countsObject.inpFaults.hvCnt = count;
                break;
            case dev_constants.EventGrpPowerFailure:
                countsObject.inpFaults.pfCnt = count;
                break;
            case dev_constants.EventGrpIsolatorFail:
                countsObject.inpFaults.isofCnt = count;
                break;
            case dev_constants.EventGrpMcbTrip:
                countsObject.outFaults.tripCnt = count;
                break;
            case dev_constants.EventGrpHighCurrent:
                countsObject.outFaults.hcCnt = count;
                break;
            case dev_constants.EventGrpContactorFailure:
                countsObject.outFaults.cfCnt = count;
                break;
            case dev_constants.EventGrpEnergyMeterFail:
                countsObject.othFaults.emfCnt = count;
                break;
            case dev_constants.EventGrpDoorOpen:
                countsObject.othFaults.doCnt = count;
                break;
            case dev_constants.EventGrpLowBattery:
                countsObject.othFaults.lbCnt = count;
                break;
            case dev_constants.EventGrpInActiveCnt:
                countsObject.othFaults.inaCnt = count;
                break;
            default:
                break;
        }
    }

    deleteCountsForExistingDevice(newDeviceData) {
        let oldDeviceData = devicesMap.get(newDeviceData.uid);
        let isDevAlrdyCached = (oldDeviceData !== undefined);

        if (!isDevAlrdyCached) {
            console.log("error: device to delete", newDeviceData.uid, "not found in cache");
            return;
        }

        if (countsObject.slcCnt > 0) {
            countsObject.slcCnt--;
        }

        const oldStatus = this.getSlcCountStatus(oldDeviceData);
        if (oldStatus === dev_constants.DeviceStatusOn) {
            if (countsObject.slcOnCnt > 0) {
                countsObject.slcOnCnt--;
            }
        } else if (oldStatus === dev_constants.DeviceStatusOff) {
            if (countsObject.slcOffCnt > 0) {
                countsObject.slcOffCnt--;
            }
        }

        countsObject.lmpCnt -= oldDeviceData.lmp_cnt;
        if (countsObject.lmpCnt < 0) {
            countsObject.lmpCnt = 0;
        }

        countsObject.lmpOffCnt -= oldDeviceData.lmp_off_cnt;
        if (countsObject.lmpOffCnt < 0) {
            countsObject.lmpOffCnt = 0;
        }

        countsObject.lmpOnCnt -= (oldDeviceData.lmp_cnt - oldDeviceData.lmp_off_cnt);
        if (countsObject.lmpOnCnt < 0) {
            countsObject.lmpOnCnt = 0;
        }

        if (oldDeviceData.err_cnt > 0) {
            if (countsObject.slcErrCnt > 0) countsObject.slcErrCnt--;
        }

        switch (oldDeviceData.dev_md) {
            case dev_constants.DeviceModeAuto:
                if (countsObject.modes.autoCnt > 0) countsObject.modes.autoCnt--;
                break;
            case dev_constants.DeviceModeManual:
                if (countsObject.modes.manCnt > 0) countsObject.modes.manCnt--;
                break;
            case dev_constants.DeviceModeBypass:
                if (countsObject.modes.bypCnt > 0) countsObject.modes.bypCnt--;
                break;
            default:
                break;
        }

        newDeviceData.errors = [];
        this.updateErrorGroupCounts(oldDeviceData, newDeviceData);

        devicesMap.delete(oldDeviceData.uid)
    }

    getDeviceErrorSelectList() {
        let errorList = [{ label: "All", value: 0 }];
        for (const errCode of deviceErrorEventCodeOrder) {
            errorList.push({
                label: deviceErrorEventCode[errCode],
                value: parseInt(errCode),
            });
        }
        return errorList;
    }

    getHeaderDetail(propName) {
        let configResponse = localStorage.getItem("firebaseConfigApiResponse");
        let detail = "";
        if (configResponse) {
            configResponse = JSON.parse(configResponse);
            switch (propName) {
                case "email":
                default: {
                    detail = localStorage.getItem("userEmail") || "";
                    break;
                }
                case "city": {
                    detail = configResponse?.displayName;
                }
            }
        }
        return detail;
    }
}

const DeviceDataService = Object.freeze(new DeviceData());
export default DeviceDataService;